import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { TbMessage2 } from 'react-icons/tb';

import { useGetByDevice } from '../../../hooks';
import { DialogTitle } from '../../dialog-title';
import { IConfirmAtom } from '../atom';

export interface IAlertProps extends Omit<IConfirmAtom, 'message'> {
  onClose: () => void;
  children: ReactNode | ReactNode[];
  color: 'primary' | 'secondary';
}

export const ConfirmDialog: FC<IAlertProps> = props => {
  const { onClose, onConfirm, children, open, title, confirmBtnLabel } = props;
  const { isMobile } = useGetByDevice();
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => confirmBtnRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const onConfirmHandler = useCallback(() => {
    onConfirm();
    onClose();
  }, [onConfirm, onClose]);

  return (
    <Dialog data-testid="confirm-dialog" open={open} onClose={onClose}>
      <DialogTitle icon={<TbMessage2 />} onClose={onClose}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ mt: 1.5 }}>
        <DialogContentText
          sx={{
            color: theme => theme.palette.grey[800],
            fontWeight: '400',
            textAlign: 'center',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {children}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: theme => `1px solid ${theme.palette.grey[300]}`,
          padding: 0,
        }}
      >
        <Button
          variant="soft"
          data-testid="confirm-dialog-ok-btn"
          sx={{
            border: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            py: isMobile ? 0.7 : 1.2,
            px: isMobile ? 4 : 7,
            fontSize: 18,
            padding: '17px',
            borderRadius: '0 0 10px 10px',
            '&:hover': {
              border: 0,
            },
            '&:focus-visible': {
              border: 0,
              outline: 'none',
              bgcolor: 'action.focus',
              boxShadow: theme => `inset 0 0 0 2px ${theme.palette.primary.main}`,
            },
          }}
          ref={confirmBtnRef}
          onClick={onConfirmHandler}
        >
          {confirmBtnLabel ?? 'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
